"use client";

import * as React from "react";
import { ChevronDownIcon, DownloadIcon, FileSpreadsheetIcon, FileTextIcon } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { useLocale, useTranslations } from "@/i18n";
import { Button } from "@ibada/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@ibada/ui";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ibada/ui";
import { Input } from "@ibada/ui";
import { Checkbox } from "@ibada/ui";
import { ActionMenuList, type ActionMenuListItem } from "@ibada/ui";
import { cn } from "@/shared/lib/utils";
import { ApiError } from "@/shared/lib/api/http";
import { useApiErrorToast } from "@/shared/hooks/use-api-error";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { useFeatureAccess } from "@/shared/feature-flags";
import type { ExportConfig } from "@/shared/components/export/export-types";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5050").replace(/\/$/, "");

/**
 * Build a local ISO 8601 timestamp with the user's timezone offset
 * baked in, e.g. `2026-06-04T14:56:00.000+01:00`. JavaScript's native
 * `toISOString()` always returns UTC (`...Z`), so we render the local
 * wall-clock components ourselves and append the offset.
 */
function formatLocalIsoWithOffset(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mins = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  const ms = date.getMilliseconds().toString().padStart(3, "0");
  // getTimezoneOffset() returns minutes WEST of UTC; flip the sign
  // to render the standard ISO 8601 east-of-UTC convention.
  const totalEastMinutes = -date.getTimezoneOffset();
  const sign = totalEastMinutes >= 0 ? "+" : "-";
  const offAbs = Math.abs(totalEastMinutes);
  const offHh = pad(Math.floor(offAbs / 60));
  const offMm = pad(offAbs % 60);
  return `${yyyy}-${mm}-${dd}T${hh}:${mins}:${ss}.${ms}${sign}${offHh}:${offMm}`;
}

type ExportFormat = "xlsx" | "pdf";

const EXTENSION: Record<ExportFormat, string> = {
  xlsx: ".xlsx",
  pdf: ".pdf"
};

function extractFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const utfMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utfMatch?.[1]) {
    try {
      return decodeURIComponent(utfMatch[1]);
    } catch {
      return utfMatch[1];
    }
  }

  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }

  return null;
}

/**
 * The export endpoints use raw `fetch` (not the shared http client), so build an `ApiError` from a
 * failed response. This lets the shared error resolver map any backend code to localized copy; the raw
 * backend message is never surfaced.
 */
async function buildExportApiError(response: Response): Promise<ApiError> {
  let issues: ApiError["issues"];
  let code: string | undefined;
  try {
    const payload = (await response.json()) as {
      code?: unknown;
      issues?: ApiError["issues"];
    };
    if (typeof payload?.code === "string") code = payload.code;
    if (payload?.issues && typeof payload.issues === "object") issues = payload.issues;
  } catch {
    // No/invalid JSON body — fall back to a status-only ApiError.
  }
  return new ApiError("export-failed", response.status, issues, code);
}

type ExportActionProps = {
  config: ExportConfig;
  className?: string;
  buttonLabel?: string;
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  buttonSize?: React.ComponentProps<typeof Button>["size"];
  /**
   * The number of records matching the screen's current filters. Once known,
   * an empty result set makes every export entry unavailable. `undefined`
   * deliberately means "still loading/unknown", rather than empty.
   */
  itemCount?: number;
  disabled?: boolean;
  /**
   * `"button"` (default): the standalone export button + format dropdown.
   * `"menuItem"`: a single row meant to live inside a toolbar `⋯` overflow menu/bottom sheet
   * (opens the export dialog directly; format/columns are chosen there).
   */
  renderAs?: "button" | "menuItem";
  /**
   * Which export formats to offer. Defaults to ["csv-flat", "xlsx-flat", "pdf"]: flat CSV + flat Excel
   * (simple, re-usable data files, exported directly with all columns) plus PDF (the formatted report via
   * the column-picker dialog). "xlsx" is the legacy formatted Excel (dialog) and is opt-in only. Pass
   * ["pdf"] when a resource supplies its own flat data exports via extraExportOptions (e.g. members).
   */
  formats?: Array<"xlsx" | "pdf" | "csv-flat" | "xlsx-flat">;
  /**
   * Extra options injected at the TOP of the export dropdown (e.g. a flat, re-importable data CSV) so a
   * resource can offer one "Export" control instead of separate buttons. Rendered as menu items in
   * `"button"` mode and as rows in the `"menuItem"` overflow mode.
   */
  extraExportOptions?: Array<{ id: string; label: string; icon?: React.ReactNode; onSelect: () => void }>;
};

/** The branch decides the heading language, so the resource lookup has to say which branch it is asking about. */
const scopeQuery = (workspaceId?: string, branchId?: string): string => {
  const params = new URLSearchParams();
  if (workspaceId) params.set("workspaceId", workspaceId);
  if (branchId) params.set("branchId", branchId);
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
};

type ResourceColumnDescriptor = {
  key: string;
  /** Already in the document's language. Rendered as-is — the browser never translates a heading. */
  label?: string;
  type?: string;
};

/**
 * Fetch the canonical column list the backend advertises for a given
 * export resource. The component intersects this with the caller's
 * `config.columns` so the picker can never offer a key the backend
 * doesn't accept — eliminating the entire "Unsupported column key"
 * class of bugs at the source.
 *
 * When the call fails (offline, 404, old backend), we silently fall
 * back to the caller-supplied list; the backend's soft-strip is the
 * safety net.
 */
function useExportResourceColumns(resource: string, workspaceId?: string, branchId?: string) {
  const { getToken } = useAuth();
  return useQuery<{ resource: string; columns: ResourceColumnDescriptor[] } | null>({
    queryKey: ["exports", "resources", resource, "columns", workspaceId ?? null, branchId ?? null],
    queryFn: async () => {
      const token = await getToken();
      try {
        const response = await fetch(
          `${API_BASE_URL}/exports/resources/${encodeURIComponent(resource)}/columns${scopeQuery(workspaceId, branchId)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (!response.ok) return null;
        return (await response.json()) as { resource: string; columns: ResourceColumnDescriptor[] };
      } catch {
        return null;
      }
    },
    // Resource schemas barely change. Cache aggressively so 6
    // ExportAction instances on one page (assets view) dedupe to a
    // single network call.
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

export function ExportAction({
  config,
  className,
  buttonLabel,
  buttonVariant = "outline",
  buttonSize = "default",
  itemCount,
  disabled,
  renderAs = "button",
  extraExportOptions,
  formats = ["csv-flat", "xlsx-flat", "pdf"],
}: ExportActionProps) {
  const t = useTranslations("common.export");
  const tGlobal = useTranslations();
  const toastError = useApiErrorToast("export");
  // Exports kill switch: hide every export control app-wide when the flag is off (the API also enforces it).
  const exportsAllowed = useFeatureAccess("exports.run").allowed;
  const locale = useLocale();
  const { getToken } = useAuth();
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const [mobileSheetOpen, setMobileSheetOpen] = React.useState(false);
  const [format, setFormat] = React.useState<ExportFormat>("xlsx");
  const [fileName, setFileName] = React.useState(config.fileName ?? "");
  /*
   * Seeded from the caller's list when there is one, and left empty when there is not.
   *
   * Empty is correct rather than unfortunate: with no caller list the columns arrive from the server, and
   * the effect below re-seeds this the moment the dialog opens. Guessing a selection before the real list
   * lands would only mean ticking keys that may not exist.
   */
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(
    () =>
      new Set(
        (config.columns ?? [])
          .filter((column) => column.defaultSelected !== false)
          .map((column) => column.key)
      )
  );
  const [isExporting, setIsExporting] = React.useState(false);
  const isEmpty = itemCount === 0;
  const isDisabled = disabled || isEmpty;

  /*
   * The server's list is the DEFAULT, not a validator.
   *
   * It arrives with each column already named in the document's language, so a screen that passes no
   * `columns` still shows a correctly translated picker — and stays correct when a column is added to the
   * resource. Callers who do pass a list are only narrowing the offer; their labels are ignored.
   */
  const { data: introspection } = useExportResourceColumns(
    config.resource,
    typeof config.query?.workspaceId === "string" ? config.query.workspaceId : undefined,
    typeof config.query?.branchId === "string" ? config.query.branchId : undefined
  );

  const serverLabelByKey = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const column of introspection?.columns ?? []) {
      if (column.label) map.set(column.key, column.label);
    }
    return map;
  }, [introspection]);

  const columns = React.useMemo(() => {
    /*
     * No caller list: offer everything the resource exports, in the server's own order and wording.
     * This is the path every screen should be on.
     */
    if (!config.columns) {
      return (introspection?.columns ?? []).map((column) => ({
        key: column.key,
        label: column.label ?? column.key
      }));
    }

    /*
     * A caller list narrows the offer. Its labels are DISCARDED whenever the server has named the column —
     * a heading picked by the browser is exactly how French pages ended up exporting English headers. The
     * legacy translation-key handling below only runs for columns the server has never heard of.
     */
    return config.columns.map((column) => {
      const serverLabel = serverLabelByKey.get(column.key);
      if (serverLabel) return { ...column, label: serverLabel };

      const rawLabel = column.label ?? "";
      if (!rawLabel || rawLabel.includes(" ") || !rawLabel.includes(".")) {
        return column;
      }

      const translated = tGlobal(rawLabel as never);
      if (translated !== rawLabel) {
        return { ...column, label: translated };
      }

      if (rawLabel.includes(".table.")) {
        const fallbackKey = rawLabel.replace(".table.", ".");
        const fallbackTranslation = tGlobal(fallbackKey as never);
        if (fallbackTranslation !== fallbackKey) {
          return { ...column, label: fallbackTranslation };
        }
      }

      return column;
    });
  }, [config.columns, introspection, serverLabelByKey, tGlobal]);

  // Keys the backend will actually accept. When introspection fails (offline, old backend) this is null and
  // the caller's list is used as-is — the backend soft-strip is the safety net.
  const allowedKeys = React.useMemo(() => {
    if (!introspection) return null;
    return new Set(introspection.columns.map((c) => c.key));
  }, [introspection]);

  const visibleColumns = React.useMemo(() => {
    if (!allowedKeys) return columns;
    const filtered = columns.filter((c) => allowedKeys.has(c.key));
    return filtered;
  }, [columns, allowedKeys]);

  React.useEffect(() => {
    if (!open) return;
    setFileName(config.fileName ?? "");
    // Default to the non-opt-in columns; redundant ones (defaultSelected: false) stay unticked but available.
    setSelectedKeys(
      new Set(
        visibleColumns
          .filter((column) => !("defaultSelected" in column) || column.defaultSelected !== false)
          .map((column) => column.key)
      )
    );
  }, [visibleColumns, config.fileName, open]);

  const allSelected = selectedKeys.size === visibleColumns.length;

  const openWithFormat = React.useCallback((next: ExportFormat) => {
    setFormat(next);
    setOpen(true);
  }, []);

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedKeys(new Set(visibleColumns.map((column) => column.key)));
    } else {
      setSelectedKeys(new Set());
    }
  };

  const toggleOne = (key: string, checked: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const handleExport = async () => {
    if (selectedKeys.size === 0) {
      toast.error(t("errors.noColumns"));
      return;
    }

    try {
      setIsExporting(true);
      const token = await getToken();
      const selectedColumns = visibleColumns.filter((column) => selectedKeys.has(column.key));
      const payload = {
        resource: config.resource,
        fileName: fileName.trim() || undefined,
        columns: selectedColumns,
        query: config.query ?? {},
        format,
      };

      // Caller context (locale + click time WITH timezone offset)
      // rides on HTTP headers so any future endpoint that needs the
      // user's language or clock — not just /exports — can read it
      // without a payload change. The body stays pure data.
      //
      // `X-Client-Time` is a full ISO 8601 timestamp including the
      // user's UTC offset (e.g. `2026-06-04T14:56:00.000+01:00`) so
      // a single header carries both the instant and the timezone.
      const clientContextHeaders: Record<string, string> = {
        "Accept-Language": locale === "fr" ? "fr" : "en",
        "X-Client-Time": formatLocalIsoWithOffset(new Date()),
      };

      const response = await fetch(`${API_BASE_URL}/exports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...clientContextHeaders,
          ...(config.headers ?? {}),
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw await buildExportApiError(response);
      }

      const blob = await response.blob();
      const ext = EXTENSION[format];
      const headerName = extractFilename(response.headers.get("content-disposition"));
      const defaultName = `${config.resource}-export${ext}`;
      const resolvedName = headerName || (fileName.trim() ? `${fileName.trim()}${ext}` : defaultName);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(t("success"));

      // Backend signals soft-stripped column keys via this header
      // (must be in the API's CORS exposeHeaders list). When present,
      // surface a non-blocking warning so admins know some requested
      // data didn't make it into the file.
      const droppedHeader = response.headers.get("x-exports-dropped-columns");
      if (droppedHeader && droppedHeader.length > 0) {
        toast.warning(
          t("warnings.droppedColumns", { keys: droppedHeader.replace(/,/g, ", ") })
        );
      }

      setOpen(false);
    } catch (error) {
      toastError(error);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Flat data export (csv-flat / xlsx-flat): exports DIRECTLY with all available columns, no column-picker
   * dialog - the simple, re-usable file. The formatted report (pdf/xlsx) keeps the dialog flow.
   */
  const handleDirectExport = React.useCallback(async (directFormat: "csv-flat" | "xlsx-flat") => {
    try {
      setIsExporting(true);
      const token = await getToken();
      const payload = {
        resource: config.resource,
        fileName: config.fileName?.trim() || undefined,
        columns: visibleColumns,
        query: config.query ?? {},
        format: directFormat,
      };
      const clientContextHeaders: Record<string, string> = {
        "Accept-Language": locale === "fr" ? "fr" : "en",
        "X-Client-Time": formatLocalIsoWithOffset(new Date()),
      };
      const response = await fetch(`${API_BASE_URL}/exports`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...clientContextHeaders,
          ...(config.headers ?? {}),
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw await buildExportApiError(response);
      }
      const blob = await response.blob();
      const ext = directFormat === "csv-flat" ? ".csv" : ".xlsx";
      const headerName = extractFilename(response.headers.get("content-disposition"));
      const resolvedName = headerName || `${config.resource}${ext}`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = resolvedName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("success"));
    } catch (error) {
      toastError(error);
    } finally {
      setIsExporting(false);
    }
  }, [config.fileName, config.headers, config.query, config.resource, getToken, locale, t, toastError, visibleColumns]);

  const dialogTitle =
    format === "pdf" ? t("titlePdf") : t("titleXlsx");
  const overflowMenuItems = React.useMemo<ActionMenuListItem[]>(() => {
    // On a phone each format says what kind of file it is (the ⋯ sheet shows it under the label).
    const formatDescription = (id: string) =>
      id.startsWith("csv") ? t("formatDescriptions.csv") : id.startsWith("xlsx") ? t("formatDescriptions.xlsx") : id === "pdf" ? t("formatDescriptions.pdf") : undefined;
    const directItems: ActionMenuListItem[] = extraExportOptions?.map((option) => ({
      id: option.id,
      label: option.label,
      description: formatDescription(option.id),
      icon: option.icon,
      onSelect: option.onSelect,
      disabled: isDisabled,
    })) ?? [];

    const rawFormattedChildren: Array<ActionMenuListItem | null> = [
      formats.includes("csv-flat")
        ? {
            id: "csv-flat",
            description: formatDescription("csv-flat"),
            label: t("formats.csv"),
            icon: <DownloadIcon />,
            onSelect: () => {
              void handleDirectExport("csv-flat");
            },
          }
        : null,
      formats.includes("xlsx-flat")
        ? {
            id: "xlsx-flat",
            description: formatDescription("xlsx-flat"),
            label: t("formats.xlsx"),
            icon: <FileSpreadsheetIcon />,
            onSelect: () => {
              void handleDirectExport("xlsx-flat");
            },
          }
        : null,
      formats.includes("xlsx")
        ? {
            id: "xlsx",
            description: formatDescription("xlsx"),
            label: t("formats.xlsx"),
            icon: <FileSpreadsheetIcon />,
            onSelect: () => openWithFormat("xlsx"),
          }
        : null,
      formats.includes("pdf")
        ? {
            id: "pdf",
            description: formatDescription("pdf"),
            label: t("formats.pdf"),
            icon: <FileTextIcon />,
            onSelect: () => openWithFormat("pdf"),
          }
        : null,
    ];
    const formattedChildren = rawFormattedChildren.filter((item): item is ActionMenuListItem => item !== null);

    if (formattedChildren.length === 0) return directItems;

    const formattedDisabled = isDisabled || visibleColumns.length === 0;

    // A submenu holding one option is a wasted tap, and its parent row can only be labelled with the
    // generic "Export" — which reads as a repeat of the flat "Export as CSV"/"Export as Excel" rows above
    // it (members passes formats={["pdf"]}). Promote the lone option to a top-level row so it names its
    // own destination ("Export as PDF").
    if (formattedChildren.length === 1) {
      return [...directItems, { ...formattedChildren[0]!, disabled: formattedDisabled }];
    }

    return [
      ...directItems,
      {
        id: "formatted-export",
        label: buttonLabel ?? t("button"),
        icon: <DownloadIcon />,
        disabled: formattedDisabled,
        children: formattedChildren,
      },
    ];
  }, [buttonLabel, extraExportOptions, formats, handleDirectExport, isDisabled, openWithFormat, t, visibleColumns.length]);

  if (!exportsAllowed) return null;

  return (
    <>
      {renderAs === "button" && isMobile ? (
        // Phone header: a round download icon beside the page's other actions; the formats open in the app's
        // bottom sheet, the same rows the ⋯ sheet shows.
        <Drawer open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
          <DrawerTrigger asChild>
            <Button
              variant='outline'
              size='icon'
              aria-label={buttonLabel ?? t("button")}
              className='size-10 rounded-full'
              disabled={isDisabled || visibleColumns.length === 0}
            >
              <DownloadIcon />
            </Button>
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader className='sr-only'>
              <DrawerTitle>{buttonLabel ?? t("button")}</DrawerTitle>
              <DrawerDescription>{buttonLabel ?? t("button")}</DrawerDescription>
            </DrawerHeader>
            <div className='mx-3 mt-2 mb-[max(1rem,env(safe-area-inset-bottom))] flex flex-col divide-y divide-border/60 overflow-hidden rounded-2xl bg-muted/50'>
              <ActionMenuList items={overflowMenuItems} close={() => setMobileSheetOpen(false)} />
            </div>
          </DrawerContent>
        </Drawer>
      ) : renderAs === "menuItem" ? (
        // Toolbar overflow rows (no DropdownMenu context required): any extra data exports as direct
        // rows, then a collapsible "Export" that reveals the formatted (xlsx/pdf) options.
        <ActionMenuList items={overflowMenuItems} />
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={buttonVariant}
              size={buttonSize}
              className={className}
              disabled={isDisabled || visibleColumns.length === 0}
            >
              <DownloadIcon />
              {buttonLabel ?? t("button")}
              <ChevronDownIcon className='ml-1 h-4 w-4 opacity-70' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end' className='min-w-[200px]'>
            {extraExportOptions && extraExportOptions.length > 0 ? (
              <>
                {extraExportOptions.map((option) => (
                  <DropdownMenuItem key={option.id} onSelect={() => option.onSelect()}>
                    {option.icon}
                    {option.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            ) : null}
            {formats.includes("csv-flat") ? (
              <DropdownMenuItem onSelect={() => { void handleDirectExport("csv-flat"); }}>
                <DownloadIcon className='mr-2 h-4 w-4' />
                {t("formats.csv")}
              </DropdownMenuItem>
            ) : null}
            {formats.includes("xlsx-flat") ? (
              <DropdownMenuItem onSelect={() => { void handleDirectExport("xlsx-flat"); }}>
                <FileSpreadsheetIcon className='mr-2 h-4 w-4' />
                {t("formats.xlsx")}
              </DropdownMenuItem>
            ) : null}
            {formats.includes("xlsx") ? (
              <DropdownMenuItem onSelect={() => openWithFormat("xlsx")}>
                <FileSpreadsheetIcon className='mr-2 h-4 w-4' />
                {t("formats.xlsx")}
              </DropdownMenuItem>
            ) : null}
            {formats.includes("pdf") ? (
              <DropdownMenuItem onSelect={() => openWithFormat("pdf")}>
                <FileTextIcon className='mr-2 h-4 w-4' />
                {t("formats.pdf")}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className='sm:max-w-[520px]'>
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{t("description")}</DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='space-y-2'>
              <label className='text-sm font-medium'>{t("fileNameLabel")}</label>
              <Input
                value={fileName}
                onChange={(event) => setFileName(event.target.value)}
                placeholder={t("fileNamePlaceholder")}
              />
            </div>

            <div className='space-y-2'>
              <div className='flex items-center justify-between gap-2'>
                <p className='text-sm font-medium'>{t("columnsLabel")}</p>
                <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(checked) => toggleAll(Boolean(checked))}
                    aria-label={t("selectAll")}
                  />
                  <span>{t("selectAll")}</span>
                </div>
              </div>
              <div className='max-h-64 space-y-2 overflow-auto rounded-md border p-3'>
                {visibleColumns.map((column) => {
                  const checked = selectedKeys.has(column.key);
                  return (
                    <label
                      key={column.key}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-2 py-1 text-sm",
                        checked ? "bg-muted/40" : "hover:bg-muted/30"
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => toggleOne(column.key, Boolean(value))}
                        aria-label={column.label}
                      />
                      <span>{column.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className='gap-2'>
            <Button variant='ghost' onClick={() => setOpen(false)} disabled={isExporting}>
              {t("cancel")}
            </Button>
            <Button onClick={() => void handleExport()} isLoading={isExporting}>
              {format === "pdf" ? t("confirmPdf") : t("confirmXlsx")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
